import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { SubscriptionService } from 'src/app/features/subscription/subscription.service';
import { PublicStakeholderDTO } from 'src/app/features/subscription/types/PublicStakeholderDTO';
import { MinProjectDTO } from 'src/app/features/subscription/types/MinProjectDTO';
import { lastValueFrom } from 'rxjs';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { ToastrNotificationService } from 'src/app/shared/services/toastr-notification.service';
import { ToastrType } from 'src/app/shared/types/ToastrType.enum';
import { LookupsDTO } from '../../types/LookupsDTO';
import { StakeholderService } from '../../services/stakeholder.service';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatSelectRetranslateDirective } from '../../directives/mat-select-retranslate.directive';
@Component({
  selector: 'app-global-edit-stakeholder',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSelectRetranslateDirective,
  ],
  templateUrl: './global-edit-stakeholder.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './global-edit-stakeholder.component.scss',
})
export class GlobalEditStakeholderComponent {
  @Input() projectInfo: MinProjectDTO | null = null;
  @Input() stakeholderInfo: PublicStakeholderDTO | null = null;
  @Input() goBackUrl: string | null = null;
  @Input() isEdit: boolean = false;
  @Input() hideButtons: boolean = false;
  @Output() formResult = new EventEmitter<any>();
  @ViewChild('noteInput') noteInput!: ElementRef<HTMLInputElement>;

  subscriptionForm: FormGroup;

  states: LookupsDTO[] = [];
  organizationTypes: LookupsDTO[] = [];
  commentTopics: LookupsDTO[] = [];
  commentChoices: LookupsDTO[] = [];
  travelTypes: LookupsDTO[] = [];
  submitInProgress: boolean = false;
  organizationTypeAnnouncement = '';
  travelAnnouncement = '';

  constructor(
    private lookupService: LookupService,
    private toastrNotification: ToastrNotificationService,
    private subscriptionService: SubscriptionService,
    private urlParamsService: UrlParamsService,
    private stakeholderService: StakeholderService,
    private fb: FormBuilder
  ) {
    this.subscriptionForm = this.fb.group({
      FirstName: ['', Validators.required],
      LastName: ['', Validators.required],
      StakeEmail: ['', [Validators.required, Validators.email]],
      Mail1AddressLine1: [''],
      Mail1AddressLine2: [''],
      Mail1City: [''],
      Mail1State: [''],
      Mail1ZipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      Organization: [''],
      TravelIds: [[]],
      HearAboutUsId: [''],
      OrganizationTypeList: [[]],
      IsReceiveEmailUpdate: true,
    });
  }

  ngOnInit(): void {
    this.loadLookups();
    this.initializeForm();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stakeholderInfo'] && !changes['stakeholderInfo'].isFirstChange()) {
      this.initializeForm();
    }
  }

  async initializeForm(): Promise<void> {
    let preservedValues = {
      StakeEmail: this.stakeholderInfo?.StakeEmail,
      LastName: this.stakeholderInfo?.LastName,
      Mail1ZipCode: this.stakeholderInfo?.Mail1ZipCode,
    };

    if (!this.stakeholderInfo?.StakeGuid && !this.urlParamsService.stakeGuid) {
      try {
        this.stakeholderInfo = await lastValueFrom(
          this.stakeholderService.getBlankStakeholder(this.urlParamsService.projectGuid!)
        );
      } catch (error) {
        console.error('Error loading blank stakeholder', error);

        return;
      }
    }

    if (this.stakeholderInfo) {
      this.subscriptionForm.patchValue({
        FirstName: this.stakeholderInfo.FirstName,
        LastName: preservedValues.LastName || this.stakeholderInfo.LastName,
        StakeEmail: preservedValues.StakeEmail || this.stakeholderInfo.StakeEmail,
        Mail1AddressLine1: this.stakeholderInfo.Mail1AddressLine1,
        Mail1AddressLine2: this.stakeholderInfo.Mail1AddressLine2,
        Mail1City: this.stakeholderInfo.Mail1City,
        Mail1State: this.stakeholderInfo.Mail1State,
        Mail1ZipCode: preservedValues.Mail1ZipCode || this.stakeholderInfo.Mail1ZipCode,
        Organization: this.stakeholderInfo.Organization,
        TravelIds: this.stakeholderInfo.TravelIds ?? [],
        OrganizationTypeList: this.stakeholderInfo.OrganizationTypeList ?? [],
        IsReceiveEmailUpdate:
          this.stakeholderInfo.IsReceiveEmailUpdate === null ||
          this.stakeholderInfo.IsReceiveEmailUpdate === undefined
            ? true
            : this.stakeholderInfo.IsReceiveEmailUpdate,
      });
    }
  }

  goBack(): void {
    if (this.goBackUrl) {
      this.urlParamsService.navigateWithParams(this.goBackUrl);
    }
  }

  triggerSubmit(): void {
    this.onSubmit();
  }

  async onSubmit(): Promise<void> {
    if (this.subscriptionForm.valid) {
      this.submitInProgress = true;

      try {
        const formValue = this.subscriptionForm.value;
        const stakeholder: PublicStakeholderDTO = {
          ...this.stakeholderInfo,
          FirstName: formValue.FirstName,
          LastName: formValue.LastName,
          StakeEmail: formValue.StakeEmail,
          Mail1AddressLine1: formValue.Mail1AddressLine1,
          Mail1AddressLine2: formValue.Mail1AddressLine2,
          Mail1City: formValue.Mail1City,
          Mail1State: formValue.Mail1State,
          Mail1ZipCode: formValue.Mail1ZipCode,
          Organization: formValue.Organization,
          TravelIds: formValue.TravelIds,
          OrganizationTypeList: formValue.OrganizationTypeList,
          IsReceiveEmailUpdate:
            formValue.IsReceiveEmailUpdate === null || formValue.IsReceiveEmailUpdate === undefined
              ? true
              : formValue.IsReceiveEmailUpdate,
          SubscribeSourceId: this.stakeholderInfo?.SubscribeSourceId ?? 0,
          ProjectGuid: this.urlParamsService.projectGuid ?? undefined,
        };
        let response;
        if (this.isEdit) {
          response = await lastValueFrom(
            this.subscriptionService.updateStakeholder(
              this.urlParamsService.stakeGuid!,
              stakeholder
            )
          );
        } else {
          response = await lastValueFrom(
            this.subscriptionService.createStakeholder(
              this.urlParamsService.projectGuid!,
              stakeholder
            )
          );
        }

        this.formResult.emit({ status: 'success', data: response });
      } catch (error) {
        console.error('Error during service call:', error);
        this.submitInProgress = false;
        this.formResult.emit({ status: 'error', data: error });
      }
    } else {
      this.markAllAsTouched();
      this.showInvalidFieldsNotification();
      this.formResult.emit({
        status: 'invalid',
        data: this.subscriptionForm.value,
      });
    }
  }

  loadLookups(): void {
    this.lookupService.getStates().subscribe({
      next: (states) => {
        this.states = states;
      },
      error: (error) => {
        console.error('Error loading states', error);
      },
    });

    this.lookupService.getOrganizationTypes().subscribe({
      next: (organizationTypes) => {
        this.organizationTypes = organizationTypes;
      },
      error: (error) => {
        console.error('Error loading organization types', error);
      },
    });

    this.lookupService.getCommentTopics().subscribe({
      next: (commentTopics) => {
        this.commentTopics = commentTopics;
      },
      error: (error) => {
        console.error('Error loading comment topics', error);
      },
    });

    this.lookupService.getCommentChoices().subscribe({
      next: (commentChoices) => {
        this.commentChoices = commentChoices;
      },
      error: (error) => {
        console.error('Error loading comment choices', error);
      },
    });
  }

  private markAllAsTouched(): void {
    Object.values(this.subscriptionForm.controls).forEach((control) => {
      control.markAsTouched();
    });
  }

  private showInvalidFieldsNotification(): void {
    const invalidFields = Object.keys(this.subscriptionForm.controls).filter(
      (key) => this.subscriptionForm.get(key)?.invalid
    );

    const fieldNames = invalidFields.map((field) => this.getFieldDisplayName(field)).join(', ');
  }

  private getFieldDisplayName(field: string): string {
    const fieldDisplayNames: { [key: string]: string } = {
      FirstName: 'First Name',
      LastName: 'Last Name',
      StakeEmail: 'Email',
      Mail1AddressLine1: 'Address Line 1',
      Mail1ZipCode: 'Zip Code',
    };

    return fieldDisplayNames[field] || field;
  }

  onOrganizationTypeOptionChange(event: MatOptionSelectionChange, type: LookupsDTO) {
    if (event.isUserInput) {
      if (event.source.selected) {
        this.organizationTypeAnnouncement = `${type.Value} selected`;
      } else {
        this.organizationTypeAnnouncement = `${type.Value} deselected`;
      }
    }
  }

  onTravelOptionChange(event: MatOptionSelectionChange, type: LookupsDTO) {
    if (event.isUserInput) {
      if (event.source.selected) {
        this.travelAnnouncement = `${type.Value} selected`;
      } else {
        this.travelAnnouncement = `${type.Value} deselected`;
      }
    }
  }
}
